import React, { useRef, useMemo, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Box, Sphere, Text, Html, Cylinder, Plane, Line } from '@react-three/drei';
import * as THREE from 'three';

const NeonLine = ({ points, color, speed = 1, delay = 0 }) => {
  const lineRef = useRef();
  const timeRef = useRef(delay);

  useFrame((state, delta) => {
    if (lineRef.current) {
      timeRef.current += delta * speed;
      
      const material = lineRef.current.material;
      if (material) {
        const pulse = Math.sin(timeRef.current * 2) * 0.5 + 0.5;
        material.opacity = 0.3 + pulse * 0.7;
        material.emissiveIntensity = 0.5 + pulse * 1.5;
      }
    }
  });

  const linePoints = useMemo(() => {
    return points.map(point => new THREE.Vector3(...point));
  }, [points]);

  return (
    <Line
      ref={lineRef}
      points={linePoints}
      color={color}
      lineWidth={3}
      transparent
      opacity={0.8}
    >
      <lineBasicMaterial
        color={color}
        emissive={color}
        emissiveIntensity={1}
        transparent
        opacity={0.8}
      />
    </Line>
  );
};

const NeonParticle = ({ path, color, speed, delay, size }) => {
  const particleMeshRef = useRef();
  const progressRef = useRef(delay);

  useFrame((state, delta) => {
    if (!particleMeshRef.current) return;

    progressRef.current += delta * speed;
    if (progressRef.current > 1) {
      progressRef.current = 0;
    }

    const pathIndex = Math.floor(progressRef.current * (path.length - 1));
    const localProgress = (progressRef.current * (path.length - 1)) - pathIndex;

    if (pathIndex < path.length - 1) {
      const start = path[pathIndex];
      const end = path[pathIndex + 1];

      const x = start[0] + (end[0] - start[0]) * localProgress;
      const y = start[1] + (end[1] - start[1]) * localProgress;
      const z = start[2] + (end[2] - start[2]) * localProgress;
      
      particleMeshRef.current.position.set(x, y, z);
    }
  });

  return (
    <Sphere ref={particleMeshRef} args={[size, 8, 8]}>
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={2}
        transparent
        opacity={0.9}
      />
    </Sphere>
  );
};

const NeonParticleSystem = () => {
  const timeRef = useRef(0);

  const neonLines = useMemo(() => {
    return [
      {
        points: [
          [-20, -6.1, -15], [-10, -6.1, -5], [0, -6.1, 0], [10, -6.1, 5], [20, -6.1, 15]
        ],
        color: '#22c55e', speed: 0.8, delay: 0
      },
      {
        points: [
          [15, -6.1, -20], [5, -6.1, -10], [0, -6.1, 0], [-5, -6.1, 10], [-15, -6.1, 20]
        ],
        color: '#06b6d4', speed: 1.2, delay: 0.5
      },
      {
        points: [
          [3.1, -6, 4], [3.1, 0, 4], [3.1, 3, 4], [3.1, 6, 4]
        ],
        color: '#8b5cf6', speed: 0.6, delay: 1
      },
      {
        points: [
          [-3.1, -6, 4], [-3.1, 0, 4], [-3.1, 3, 4], [-3.1, 6, 4]
        ],
        color: '#ec4899', speed: 0.7, delay: 1.5
      },
      {
        points: [
          [-3, -6, 4], [0, 0, 4], [3, 6, 4]
        ],
        color: '#f59e0b', speed: 0.9, delay: 2
      },
      {
        points: [
          [3, -6, 4], [0, 0, 4], [-3, 6, 4]
        ],
        color: '#ef4444', speed: 1.1, delay: 2.5
      },
      {
        points: Array.from({ length: 20 }, (_, i) => {
          const angle = (i / 20) * Math.PI * 2;
          const radius = 8;
          return [Math.cos(angle) * radius, -6.1, Math.sin(angle) * radius];
        }),
        color: '#10b981', speed: 0.5, delay: 3
      },
      {
        points: [
          [0, -6, 8], [0, 0, 8], [0, 3, 8], [0, 6, 8], [0, 8, 8]
        ],
        color: '#3b82f6', speed: 1.5, delay: 0.3
      },
      {
        points: [
          [-8, 0, 8], [-4, 0, 8], [0, 0, 8], [4, 0, 8], [8, 0, 8]
        ],
        color: '#f97316', speed: 1.3, delay: 0.8
      }
    ];
  }, []);

  const particles = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      path: [
        [
          [-15 + i * 3, -6, -10], [-10 + i * 2, -3, -5], [-5 + i * 1.5, 0, 0], [0, 3, 5], [5 - i * 0.5, 6, 10]
        ],
        [
          [10 - i * 2, -6, 15], [5 - i * 1.5, -2, 10], [0, 2, 5], [-5 + i, 4, 0], [-10 + i * 1.5, 6, -5]
        ]
      ][i % 2],
      color: ['#22c55e', '#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b'][i % 5],
      speed: 0.5 + (i * 0.1),
      delay: i * 0.2,
      size: 0.05 + (i * 0.01)
    }));
  }, []);

  useFrame((state, delta) => {
    timeRef.current += delta;
  });

  return (
    <group>
      {neonLines.map((line, index) => (
        <NeonLine
          key={`line-${index}`}
          points={line.points}
          color={line.color}
          speed={line.speed}
          delay={line.delay}
        />
      ))}
      {particles.map((particle) => (
        <NeonParticle
          key={`particle-${particle.id}`}
          path={particle.path}
          color={particle.color}
          speed={particle.speed}
          delay={particle.delay}
          size={particle.size}
        />
      ))}
    </group>
  );
};

const Drone = ({ position, color, delay = 0, onHover, orbitRadius = 4, orbitSpeed = 0.5 }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const timeRef = useRef(delay);

  useFrame((state) => {
    if (meshRef.current) {
      timeRef.current += state.clock.getDelta();
      
      const angle = timeRef.current * orbitSpeed;
      const x = Math.cos(angle) * orbitRadius;
      const z = Math.sin(angle) * orbitRadius;
      const floatY = Math.sin(timeRef.current * 2) * 0.2;
      
      meshRef.current.position.set(position[0] + x, position[1] + floatY, position[2] + z);
      
      meshRef.current.lookAt(position[0], position[1] + floatY, position[2]);
    }
  });

  return (
    <group
      ref={meshRef}
      onPointerOver={() => {
        setHovered(true);
        onHover(true);
      }}
      onPointerOut={() => {
        setHovered(false);
        onHover(false);
      }}
    >
      <Box args={[0.8, 0.3, 0.8]} position={[0, 0, 0]}>
        <meshStandardMaterial color={hovered ? '#22c55e' : color} metalness={0.7} roughness={0.3} />
      </Box>
      <Sphere args={[0.2, 8, 8]} position={[0, 0.2, 0]}>
        <meshStandardMaterial color="#1e293b" metalness={0.9} roughness={0.1} />
      </Sphere>
      <Box args={[0.08, 0.04, 1.6]} position={[0.6, 0, 0.6]}>
        <meshStandardMaterial color="#374151" metalness={0.6} roughness={0.4} />
      </Box>
      <Box args={[0.08, 0.04, 1.6]} position={[-0.6, 0, -0.6]}>
        <meshStandardMaterial color="#374151" metalness={0.6} roughness={0.4} />
      </Box>
      <Box args={[1.6, 0.04, 0.08]} position={[0.6, 0, -0.6]}>
        <meshStandardMaterial color="#374151" metalness={0.6} roughness={0.4} />
      </Box>
      <Box args={[1.6, 0.04, 0.08]} position={[-0.6, 0, 0.6]}>
        <meshStandardMaterial color="#374151" metalness={0.6} roughness={0.4} />
      </Box>
      <Cylinder args={[0.15, 0.15, 0.1]} position={[0.6, 0.1, 0.6]}>
        <meshStandardMaterial color="#6b7280" metalness={0.8} roughness={0.2} />
      </Cylinder>
      <Cylinder args={[0.15, 0.15, 0.1]} position={[-0.6, 0.1, -0.6]}>
        <meshStandardMaterial color="#6b7280" metalness={0.8} roughness={0.2} />
      </Cylinder>
      <Cylinder args={[0.15, 0.15, 0.1]} position={[0.6, 0.1, -0.6]}>
        <meshStandardMaterial color="#6b7280" metalness={0.8} roughness={0.2} />
      </Cylinder>
      <Cylinder args={[0.15, 0.15, 0.1]} position={[-0.6, 0.1, 0.6]}>
        <meshStandardMaterial color="#6b7280" metalness={0.8} roughness={0.2} />
      </Cylinder>
      <group>
        <Plane args={[0.4, 0.04]} position={[0.6, 0.2, 0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.04, 0.4]} position={[0.6, 0.2, 0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.4, 0.04]} position={[-0.6, 0.2, -0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.04, 0.4]} position={[-0.6, 0.2, -0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.4, 0.04]} position={[0.6, 0.2, -0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.04, 0.4]} position={[0.6, 0.2, -0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.4, 0.04]} position={[-0.6, 0.2, 0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
        <Plane args={[0.04, 0.4]} position={[-0.6, 0.2, 0.6]} rotation={[0, Math.PI / 4, 0]}>
          <meshStandardMaterial color="#94a3b8" emissive="#94a3b8" emissiveIntensity={0.3} transparent opacity={0.7} />
        </Plane>
      </group>
      <Cylinder args={[0.12, 0.12, 0.6]} position={[0, -0.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#3b82f6" metalness={0.6} roughness={0.4} />
      </Cylinder>
      <Cylinder args={[0.04, 0.04, 2]} position={[0, -1.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#1e40af" metalness={0.3} roughness={0.7} />
      </Cylinder>
      <Cylinder args={[0.08, 0.06, 0.3]} position={[0, -2.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#dc2626" metalness={0.8} roughness={0.2} />
      </Cylinder>
      <Sphere args={[0.05, 6, 6]} position={[0.3, -0.15, 0]}>
        <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={hovered ? 1 : 0.5} />
      </Sphere>
      <Sphere args={[0.05, 6, 6]} position={[-0.3, -0.15, 0]}>
        <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={hovered ? 1 : 0.5} />
      </Sphere>
      {hovered && (
        <Html position={[0, 1.5, 0]} center>
          <div className="bg-slate-900/90 text-white px-4 py-2 rounded-lg text-sm backdrop-blur-sm border border-green-400/30">
            <div className="font-semibold text-green-400">Professional Drone</div>
            <div className="text-xs text-slate-300">Water-based cleaning system</div>
          </div>
        </Html>
      )}
    </group>
  );
};

const RealisticBuilding = () => {
  const buildingRef = useRef();
  
  const windows = useMemo(() => {
    const windowArray = [];
    const floors = 15;
    const windowsPerFloor = 10;
    
    for (let floor = 0; floor < floors; floor++) {
      for (let window = 0; window < windowsPerFloor; window++) {
        windowArray.push({
          position: [
            (window - windowsPerFloor / 2 + 0.5) * 0.5,
            (floor - floors / 2 + 0.5) * 0.7,
            4.01
          ],
          lit: Math.random() > 0.4
        });
      }
    }
    
    return windowArray;
  }, []);

  return (
    <group ref={buildingRef} position={[0, 0, 0]}>
      <Box args={[6, 12, 8]} position={[0, 0, 0]} receiveShadow castShadow>
        <meshStandardMaterial 
          color="#1e40af" 
          metalness={0.9} 
          roughness={0.1} 
          envMapIntensity={1.5}
          transparent
          opacity={0.85}
        />
      </Box>
      <Box args={[6.01, 12.01, 0.1]} position={[0, 0, 4.05]}>
        <meshStandardMaterial 
          color="#0ea5e9" 
          metalness={0.95} 
          roughness={0.05} 
          envMapIntensity={2}
          transparent
          opacity={0.7}
        />
      </Box>
      {windows.map((window, index) => (
        <group key={index}>
          <Box args={[0.35, 0.45, 0.02]} position={window.position}>
            <meshStandardMaterial 
              color={window.lit ? '#fef3c7' : '#1e40af'} 
              emissive={window.lit ? '#fef3c7' : '#0ea5e9'} 
              emissiveIntensity={window.lit ? 0.3 : 0.1} 
              metalness={window.lit ? 0.2 : 0.8}
              roughness={window.lit ? 0.8 : 0.15}
              envMapIntensity={window.lit ? 0.5 : 1.5}
            />
          </Box>
          <Box args={[0.38, 0.48, 0.01]} position={[window.position[0], window.position[1], window.position[2] + 0.015]}>
            <meshStandardMaterial 
              color="#0284c7" 
              metalness={0.9} 
              roughness={0.1}
              envMapIntensity={1.8}
            />
          </Box>
        </group>
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <Box 
          key={`panel-${i}`} 
          args={[0.8, 12, 0.05]} 
          position={[
            (i - 3.5) * 0.75, 
            0, 
            4.03
          ]} 
        >
          <meshStandardMaterial 
            color="#0c4a6e" 
            metalness={0.85} 
            roughness={0.15}
            envMapIntensity={1.2}
          />
        </Box>
      ))}
      <Box args={[6.2, 0.4, 8.2]} position={[0, 6.2, 0]}>
        <meshStandardMaterial 
          color="#1e40af" 
          metalness={0.7} 
          roughness={0.2}
          envMapIntensity={1}
        />
      </Box>
      <Box args={[1, 1.5, 1]} position={[-2, 6.8, -2]}>
        <meshStandardMaterial 
          color="#2563eb" 
          metalness={0.8} 
          roughness={0.15}
          envMapIntensity={1.3}
        />
      </Box>
      <Box args={[1, 1.5, 1]} position={[2, 6.8, -2]}>
        <meshStandardMaterial 
          color="#2563eb" 
          metalness={0.8} 
          roughness={0.15}
          envMapIntensity={1.3}
        />
      </Box>
      <Box args={[2, 4, 0.1]} position={[0, -4, 4.05]}>
        <meshStandardMaterial 
          color="#1e3a8a" 
          metalness={0.9} 
          roughness={0.1}
          envMapIntensity={1.5}
        />
      </Box>
      <Box args={[0.8, 3, 0.05]} position={[-0.5, -4, 4.07]}>
        <meshStandardMaterial 
          color="#1d4ed8" 
          metalness={0.95} 
          roughness={0.05}
          envMapIntensity={2}
        />
      </Box>
      <Box args={[0.8, 3, 0.05]} position={[0.5, -4, 4.07]}>
        <meshStandardMaterial 
          color="#1d4ed8" 
          metalness={0.95} 
          roughness={0.05}
          envMapIntensity={2}
        />
      </Box>
    </group>
  );
};

const Ground = () => {
  return (
    <group>
      <Box args={[50, 0.2, 50]} position={[0, -6.2, 0]} receiveShadow>
        <meshStandardMaterial color="#374151" metalness={0.1} roughness={0.9} />
      </Box>
      <Box args={[50, 0.21, 0.05]} position={[0, -6.19, 0]}>
        <meshStandardMaterial color="#4b5563" />
      </Box>
      <Box args={[0.05, 0.21, 50]} position={[0, -6.19, 0]}>
        <meshStandardMaterial color="#4b5563" />
      </Box>
      <Box args={[10, 0.05, 12]} position={[0, -6.09, 0]}>
        <meshStandardMaterial color="#6b7280" metalness={0.1} roughness={0.8} />
      </Box>
    </group>
  );
};

const CleaningEffect = ({ active, dronePosition, buildingPosition = [0, 0, 0] }) => {
  const particlesRef = useRef([]);
  
  if (particlesRef.current.length === 0 && active) {
    particlesRef.current = Array.from({ length: 60 }).map(() => ({
      position: [
        dronePosition[0] + (Math.random() - 0.5) * 0.8,
        dronePosition[1] - 2,
        dronePosition[2] + (Math.random() - 0.5) * 0.8
      ],
      velocity: [
        (Math.random() - 0.5) * 0.02,
        -Math.random() * 0.04 - 0.02,
        (Math.random() - 0.5) * 0.02
      ],
      life: Math.random() * 1.5 + 0.5,
      size: Math.random() * 0.03 + 0.02
    }));
  }

  useFrame((state, delta) => {
    if (!active) return;
    
    particlesRef.current.forEach((particle) => {
      particle.position[0] += particle.velocity[0];
      particle.position[1] += particle.velocity[1];
      particle.position[2] += particle.velocity[2];
      particle.life -= delta;
      
      if (particle.life <= 0) {
        const angle = Math.atan2(dronePosition[2] - buildingPosition[2], dronePosition[0] - buildingPosition[0]);
        particle.position[0] = dronePosition[0] + Math.cos(angle) * 0.5;
        particle.position[1] = dronePosition[1] - 2;
        particle.position[2] = dronePosition[2] + Math.sin(angle) * 0.5;
        particle.velocity = [
          -Math.cos(angle) * 0.03 + (Math.random() - 0.5) * 0.01,
          -Math.random() * 0.04 - 0.02,
          -Math.sin(angle) * 0.03 + (Math.random() - 0.5) * 0.01
        ];
        particle.life = Math.random() * 1.5 + 0.5;
      }
    });
  });

  if (!active) return null;

  return (
    <group>
      {particlesRef.current.map((particle, i) => (
        <Sphere key={i} args={[particle.size, 4, 4]} position={particle.position}>
          <meshStandardMaterial 
            color="#60a5fa" 
            emissive="#60a5fa" 
            emissiveIntensity={0.4} 
            transparent 
            opacity={0.7} 
          />
        </Sphere>
      ))}
    </group>
  );
};

const Scene3D = () => {
  const [hoveredDrone, setHoveredDrone] = useState(null);
  
  const dronePositions = useMemo(() => [
    { position: [0, 3, 0], color: '#06b6d4', id: 'drone1', orbitRadius: 6, orbitSpeed: 0.5 },
    { position: [0, 6, 0], color: '#8b5cf6', id: 'drone2', orbitRadius: 6, orbitSpeed: -0.4 }
  ], []);

  return (
    <group>
      <ambientLight intensity={0.3} />
      <directionalLight position={[15, 20, 10]} intensity={2} castShadow />
      <pointLight position={[-15, 15, -10]} color="#22c55e" intensity={0.8} />
      <pointLight position={[15, 15, 15]} color="#06b6d4" intensity={0.6} />
      <spotLight position={[0, 20, 0]} angle={0.4} penumbra={1} intensity={0.8} color="#fbbf24" />
      
      <Ground />
      <NeonParticleSystem />
      <RealisticBuilding />
      
      {dronePositions.map((drone) => (
        <Drone 
          key={drone.id}
          position={drone.position} 
          color={drone.color} 
          delay={0} 
          orbitRadius={drone.orbitRadius} 
          orbitSpeed={drone.orbitSpeed} 
          onHover={(hovered) => setHoveredDrone(hovered ? drone.id : null)} 
        />
      ))}
      
      {dronePositions.map((drone) => (
        <CleaningEffect 
          key={`effect-${drone.id}`}
          active={true} 
          dronePosition={drone.position} 
          buildingPosition={[0, 0, 0]} 
        />
      ))}
    </group>
  );
};

export default Scene3D;